<template>
    <app-layout
        :silent-login-enabled="config.silentLoginEnabled"
        :error-boundary-options="errorBoundaryOptions"
        :v-app-options="{ class: 'minside-app bg-grey-darken-1 bg-bg-primary' }"
    >
        <v-main v-if="mainStore.ready" class="d-flex flex-column align-center justify-center w-100">
            <v-container max-width="1300" class="pa-0 h-100">
                <router-view />
            </v-container>
        </v-main>

        <template #footer>
            <footer-area />
        </template>
    </app-layout>
</template>

<script setup lang="ts">
import { useRoute, useRouter } from 'vue-router';
import { watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useMainStore } from '@/features/common/stores';
import { AppLayout } from '@geta/kolumbus-frontend/components';
import { useUserStore } from '@geta/kolumbus-frontend/features/authentication';
import { isAnonymousRoute, routeNames } from '@/router';
import { FooterArea } from './features/FooterArea';
import { i18nRoute } from '@/i18n';
import { useAppStore } from '@geta/kolumbus-frontend/features/common';

const errorBoundaryOptions = {
    errorsToDisplay: ['ProfileError']
};

const route = useRoute();
const router = useRouter();
const appStore = useAppStore();
const mainStore = useMainStore();
const userStore = useUserStore();
const { language } = storeToRefs(appStore);
const { config } = storeToRefs(mainStore);

function redirectToCompleteProfileIfNeeded() {
    if (userStore.isLoggedIn && !userStore.profile.profileExists && userStore.profile.email !== '') {
        if (route.name !== routeNames.completeProfile) {
            router.push(i18nRoute({ name: routeNames.completeProfile }));
        }
    }
}

watch(language, async (value, oldValue) => {
    if (value !== oldValue) {
        try {
            await router.replace({ params: { locale: value }, query: route.query });
        } catch (e) {
            mainStore.registerError(e);
            router.push('/');
        }
    }
});

watch(
    () => appStore.ready,
    () => {
        // if user is not logged in and not on an anonymous route, redirect to login page
        if (!userStore.isLoggedIn && !isAnonymousRoute(route.name as string)) {
            //redirect to login page
            window.location.href = `/Authenticate?returnUrl=${encodeURI(window.location.href)}`;
            return;
        }

        // redirect to complete profile if needed
        redirectToCompleteProfileIfNeeded();

        mainStore.ready = true;
    },
    {
        once: true
    }
);
</script>

<style lang="scss">
.minside-app {
    background-color: var(--v-color-grey-lighten-3);
    &__header {
        z-index: 1000;
    }
}

.v-main {
    z-index: 1;
}
</style>
